import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Callout, Spinner, Table, type TableColumn, TextField } from '@humanforce/mango-ui-react';
import { PageTitle } from '../components/layout/PageTitle';
import { MangoSelectField } from '../features/workers/components/MangoSelectField';
import { WorkerStatusTag } from '../features/workers/components/WorkerStatusTag';
import { listWorkers, type WorkerListItem } from '../features/workers/workersApi';
import type { PersonStatus } from '../features/workers/workersApiTypes';

const PAGE_SIZE = 20;

type Row = WorkerListItem & Record<string, unknown>;

const formatDob = (value?: string) => (value ? value.slice(0, 10) : '—');

export const WorkerListPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<PersonStatus>('ACTIVE');
  const [skip, setSkip] = useState(0);
  const [workers, setWorkers] = useState<WorkerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    (async () => {
      const result = await listWorkers({ status, take: PAGE_SIZE, skip });
      if (ignore) return;
      if (result.ok) setWorkers(result.items);
      else setError(result.message ?? 'Failed to load workers.');
      setLoading(false);
    })();
    return () => { ignore = true; };
  }, [status, skip]);

  const visibleRows = useMemo<Row[]>(() => {
    const term = search.trim().toLowerCase();
    const source = workers as Row[];
    const filtered = term
      ? source.filter((w) => w.legalName.toLowerCase().includes(term) || (w.globalEmployeeId ?? '').toLowerCase().includes(term) || (w.preferredName ?? '').toLowerCase().includes(term))
      : source;
    return [...filtered].sort((a, b) => a.legalName.localeCompare(b.legalName));
  }, [workers, search]);

  const columns: TableColumn[] = [
    {
      field: 'legalName',
      header: 'Worker',
      sortable: true,
      body: (row: Row) => (
        <div className="mgo-stack mgo-gap-2xs">
          <span className="mgo-text-s mgo-weight-strong">{row.legalName}</span>
          {row.preferredName && <span className="mgo-text-xs mgo-color-muted">Preferred: {row.preferredName}</span>}
        </div>
      ),
    },
    { field: 'globalEmployeeId', header: 'Global employee ID', sortable: true, body: (row: Row) => row.globalEmployeeId ?? '—' },
    { field: 'status', header: 'Status', sortable: true, body: (row: Row) => <WorkerStatusTag status={row.status} /> },
    { field: 'dateOfBirth', header: 'Date of birth', sortable: true, body: (row: Row) => formatDob(row.dateOfBirth) },
    {
      field: 'actions',
      header: 'Actions',
      body: (row: Row) => (
        <div className="mgo-cluster mgo-gap-s">
          <Button variant="tertiary" size="small" label="Edit" onClick={() => navigate(`/workers/worker-form?worker=${row.personId}`)} />
          <Button variant="secondary" size="small" label="Open record" onClick={() => navigate(`/workers/worker-record/personal?worker=${row.personId}`)} />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Workers"
        subtitle="People records — view, add and edit worker profiles."
        actions={<Button variant="primary" icon="add" label="Add a new worker" onClick={() => navigate('/workers/worker-form')} />}
      />

      <div className="mgo-cluster mgo-gap-l mgo-align-end" style={{ marginBottom: 'var(--mgo-space-40)' }}>
        <div style={{ minWidth: '18rem' }}>
          <TextField
            label="Search"
            placeholder="Name or employee ID"
            leadingIcon="search"
            clearable
            helpText="Filters the workers loaded on this page."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
        </div>
        <div style={{ minWidth: '12rem' }}>
          <MangoSelectField
            label="Status"
            value={status}
            options={[{ label: 'Active', value: 'ACTIVE' }, { label: 'Terminated', value: 'TERMINATED' }]}
            onChange={(value) => { setStatus(value as PersonStatus); setSkip(0); }}
          />
        </div>
      </div>

      {error && <Callout tone="critical" title="Couldn't load workers">{error}</Callout>}

      {loading ? (
        <div className="mgo-cluster mgo-gap-s mgo-align-center" style={{ padding: 'var(--mgo-space-60)' }}>
          <Spinner /> <span className="mgo-text-s mgo-color-muted">Loading workers…</span>
        </div>
      ) : visibleRows.length === 0 ? (
        <Callout tone="neutral" title="No workers found">
          {search.trim() ? 'No workers on this page match your search.' : status === 'ACTIVE' ? 'There are no active workers yet. Use "Add a new worker" to create one.' : 'There are no terminated workers.'}
        </Callout>
      ) : (
        <Table value={visibleRows} columns={columns} testId="workers-table" />
      )}

      <div className="mgo-cluster mgo-justify-end mgo-gap-s mgo-align-center" style={{ marginTop: 'var(--mgo-space-40)' }}>
        <span className="mgo-text-xs mgo-color-muted">Showing {visibleRows.length} of up to {PAGE_SIZE} per page</span>
        <Button variant="secondary" size="small" label="Previous" disabled={skip === 0 || loading} onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))} />
        <Button variant="secondary" size="small" label="Next" disabled={loading || workers.length < PAGE_SIZE || visibleRows.length === 0} onClick={() => setSkip((s) => s + PAGE_SIZE)} />
      </div>
    </>
  );
};
